import { Controller } from "@hotwired/stimulus"

// select2-style multiselect over a native <select multiple>. The native select
// stays the source of truth (form submission + a11y baseline); we hide it and
// render a chip box + dropdown, mirroring every change back. No JS → the plain
// <select multiple> still works.
export default class extends Controller {
  static targets = ["select"]
  static values = { placeholder: { type: String, default: "Auswählen …" } }

  connect() {
    this.selectTarget.hidden = true
    this.element.appendChild(this.build())
    document.addEventListener("click", this.onOutside)
    this.render()
  }

  disconnect() {
    document.removeEventListener("click", this.onOutside)
    this.widget?.remove()
    this.selectTarget.hidden = false
  }

  build() {
    this.widget = document.createElement("div")
    this.widget.className = "ms"
    this.widget.innerHTML = `
      <div class="ms__control" tabindex="0" role="combobox" aria-haspopup="listbox" aria-expanded="false">
        <div class="ms__chips"></div>
        <span class="ms__arrow" aria-hidden="true">▾</span>
      </div>
      <div class="ms__menu" role="listbox" aria-multiselectable="true" hidden></div>`
    this.control = this.widget.querySelector(".ms__control")
    this.chips = this.widget.querySelector(".ms__chips")
    this.menu = this.widget.querySelector(".ms__menu")
    this.control.addEventListener("click", () => this.toggle())
    this.control.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this.toggle() }
      else if (e.key === "Escape") this.close()
    })
    return this.widget
  }

  get options() { return Array.from(this.selectTarget.options) }

  toggle() { this.menu.hidden ? this.open() : this.close() }
  open() { this.menu.hidden = false; this.control.setAttribute("aria-expanded", "true") }
  close() { this.menu.hidden = true; this.control.setAttribute("aria-expanded", "false") }

  onOutside = (e) => {
    // Ignore clicks on nodes our own re-render just detached (e.g. the option
    // just picked) — they'd otherwise read as an outside click and close us.
    if (!e.target.isConnected) return
    if (!this.element.contains(e.target)) this.close()
  }

  set(opt, selected) {
    opt.selected = selected
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.render()
  }

  render() {
    // Chips for the selected options (placeholder when none).
    this.chips.replaceChildren()
    const selected = this.options.filter((o) => o.selected)
    if (selected.length === 0) {
      const ph = document.createElement("span")
      ph.className = "ms__placeholder"
      ph.textContent = this.placeholderValue
      this.chips.append(ph)
    }
    for (const opt of selected) {
      const chip = document.createElement("span")
      chip.className = "ms__chip"
      const label = document.createElement("span")
      label.textContent = opt.text
      const x = document.createElement("button")
      x.type = "button"
      x.className = "ms__x"
      x.setAttribute("aria-label", `${opt.text} entfernen`)
      x.textContent = "×"
      x.addEventListener("click", (e) => { e.stopPropagation(); this.set(opt, false) })
      chip.append(label, x)
      this.chips.append(chip)
    }

    // Menu lists only the unselected options (select2 pulls picked ones out).
    this.menu.replaceChildren()
    const available = this.options.filter((o) => !o.selected)
    if (available.length === 0) {
      const empty = document.createElement("div")
      empty.className = "ms__empty"
      empty.textContent = "Alles ausgewählt"
      this.menu.append(empty)
    }
    for (const opt of available) {
      const item = document.createElement("div")
      item.className = "ms__option"
      item.setAttribute("role", "option")
      item.textContent = opt.text
      item.addEventListener("click", () => this.set(opt, true))
      this.menu.append(item)
    }
  }
}
